import { formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'
import { buildBaseConfig } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/baseConfig'
import { sortData } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/sortData'
import {
  removeLogicAndTruncate,
  transformText
} from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'
import { wrapTooltip } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/tooltipFormatter'
import { linkRowFromPoint } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/pointHalo'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const buildPieIngot = () =>
  buildBaseConfig({
    chart: {
      animation: false,
      backgroundColor: 'transparent',
      borderWidth: 0,
      margin: [5, 30, 10, 30],
      shadow: false
    },
    legend: {
      useHTML: true
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        center: ['50%', '50%'],
        dataLabels: {
          enabled: true,
          // the count rides under the slice's own label, outside the ring -- the share is what the
          // ring already draws, so the figure worth printing is the one it cannot show
          formatter() {
            const label = this.point.pointText != null && this.point.pointText !== '' ? this.point.pointText : ''
            const count = this.point.amount == null ? '' : formatAmounts(this.point.amount)
            if (!count) return label || (this.point.y > 0 ? `${this.point.y}%` : '')
            return label ? `${label}<br/><span style="color:#7c8899">${count}</span>` : count
          },
          distance: 20
        },
        // Hovering a slice hands it two jobs, as a column has: its own label steps aside for the
        // tooltip about to name the same slice, and its row in the list below tints. The connector
        // goes with the label -- left behind it is a line pointing at nothing.
        point: {
          events: {
            mouseOut() {
              this.dataLabel?.show()
              this.connector?.show()
              linkRowFromPoint(this.series?.chart, null)
            },
            mouseOver() {
              this.dataLabel?.hide()
              this.connector?.hide()
              linkRowFromPoint(this.series?.chart, this.id)
            }
          }
        },
        size: '80%'
      },
      series: {
        shadow: false,
        states: {
          hover: { brightness: 0.05 },
          inactive: { opacity: 1 }
        }
      }
    },
    series: [{ type: 'pie', data: [] }],
    tooltip: {
      formatter() {
        return this.point.desc === '' ? false : wrapTooltip(this.point.desc)
      }
    }
  })

const buildTooltip = ({ answerStats, refPrefix, truncatedText, ignorePercents, percentDecimals }) => {
  const percentsHtml = ignorePercents ? '' : `<div><b>${answerStats.percents.toFixed(percentDecimals ?? 1)}</b>%</div>`
  const nameHtml = `<b>${refPrefix}</b> ${truncatedText}`

  if (answerStats.unitLabel === '') {
    return `${percentsHtml}<div class='highcharts-tooltip-text'>${nameHtml}</div>`
  }

  const amount = formatAmounts(answerStats.amount)
  const unitLabel = answerStats.unitLabel ?? pluralize(Number(amount), 'respondent', 'respondents')
  const respHtml = `<b>${amount}</b> ${unitLabel}`

  return `${percentsHtml}<div class='highcharts-tooltip-text'>${respHtml}</div><div>${nameHtml}</div>`
}

const calcPieData = props => {
  // the reader's percentage precision, handed to every chart from the container
  const percentDecimals = props.reportPercentDecimals

  const {
    answer = {},
    answerVariants: rawAV = [],
    sortOrder = 'iasc',
    qType,
    scaleTmpl,
    degree,
    colorTheme = [],
    chartLegendMode,
    ignorePercents,
    ignoreRefs: _ignoreRefs,
    answerLabels
  } = props

  const ignoreRefs = _ignoreRefs || answerLabels === 'hide'
  const answerVariants = sortData(answer, [...rawAV], sortOrder)
  const positions = rawAV.filter(av => av != null).map(av => av.position)

  const columns = answerVariants
    .map((av, index) => {
      if (av == null) return null

      const position = av.position || 0
      const colorIndex = positions.indexOf(position)

      return {
        index,
        colorIndex,
        key: `a${position}`,
        position,
        color: colorTheme[colorIndex % colorTheme.length]
      }
    })
    .filter(Boolean)

  return columns
    .map(({ index, colorIndex, key, color }) => {
      const av = answerVariants[index]
      const answerStats = answer[key] || { amount: 0, percents: 0 }

      // only a positive share draws a slice: an answer that carries no percents at all is not one
      if (!av || !(0 < answerStats.percents)) return null

      const avText = qType === 'ps' ? scaleTmpl.replace(degree, av.text) : av.text
      const refPrefix = ignoreRefs ? '' : `A${av.position}: `
      const truncatedText = removeLogicAndTruncate(avText, 100)
      const pointText = chartLegendMode === 'short' && !ignoreRefs ? '' : transformText(String(avText))

      return {
        y: Number(answerStats.percents.toFixed(1)),
        amount: answerStats.amount,
        // the same key the table row carries, so a hovered row can find its slice
        id: `a${av.position}`,
        name: `A${av.position}`,
        desc: buildTooltip({ answerStats, refPrefix, truncatedText, ignorePercents, percentDecimals }),
        pointText,
        color,
        colorIndex
      }
    })
    .filter(Boolean)
}

export const buildPieOptions = (props = {}) => {
  const data = buildPieIngot()

  data.series[0].data = calcPieData(props)

  // a lone slice is the whole ring, so pulling it out would only shift the pie off its centre
  data.plotOptions.pie.allowPointSelect = data.series[0].data.length > 1

  if (data.chart) {
    data.chart.width = props.width
    data.chart.height = props.height || CHART_HEIGHT
  }

  return { options: data }
}
